/**
 * Plausibility verification — shared engine and config types.
 *
 * The verifier decides how much to believe a VLM detection by adding a prior
 * (how likely this class is at this camera, at this time of day and year) to
 * the evidence the footage actually shows (log likelihood ratios), then turning
 * that belief into a routing decision through a per-class cost matrix.
 *
 * Every number here is produced by our own code from tables we own. The model
 * only ever emits the bounded enums below (`ObservationValue`,
 * `ObservationStrength`, a class id ranking). If a probability or a weight is
 * ever coming out of an LLM call, that is a bug.
 */

import type { Ref } from "./data";

// ---------------------------------------------------------------------------
// Scope
// ---------------------------------------------------------------------------

/**
 * Where a config row applies. Resolution is most-specific-wins:
 * sensor → zone → installation → global. Global rows live in the admin
 * database; the other three live in the tenant database and reference their
 * scope by Mongo UUID.
 */
export type VerificationScopeType =
	| "global"
	| "installation"
	| "sensor"
	| "zone";

/** Rank used when two rows compete: higher wins. */
export const VERIFICATION_SCOPE_RANK: Record<VerificationScopeType, number> = {
	global: 0,
	installation: 1,
	sensor: 3,
	zone: 2,
};

/** The blind pass's answer: what it thinks the clip shows, with no knowledge of Stage 1. */
export interface BlindRanking {
	ranked: { classId: string; rank: number; support?: ObservationStrength }[];
	/** `ranked[0].classId`, or "none" when the model saw nothing listed. */
	topClassId: string;
}

// ---------------------------------------------------------------------------
// Local time context
// ---------------------------------------------------------------------------

/**
 * `alias`: the alias table answered on its own. `model`: the table missed and
 * the classifier answered. `disambiguated`: the table hit more than one
 * alertable class and the classifier chose between them. `none`: nothing.
 */
export type CanonicalizationMethod =
	| "alias"
	| "disambiguated"
	| "model"
	| "none";
export interface CanonicalizationResult {
	/** Whether the resolved class is one the verifier should investigate. */
	alertable: boolean;
	/** Other classes that also matched, for review. */
	alternates?: { classId: string; field: string; matchedAlias: string }[];
	/** Resolved class id, or `UNKNOWN_CLASS_ID` when nothing matched. */
	classId: string;
	matchedAlias?: string;
	matchedField?: "description" | "labels" | "type";
	method: CanonicalizationMethod;
	/** Raw model response when the classifier call ran. */
	modelRaw?: unknown;
}

/**
 * Relative cost of getting a class wrong in each direction. Only the ratio
 * matters: the Bayes-optimal escalation threshold is FP / (FP + FN).
 */
export interface ClassCosts {
	falseNegative: number;
	falsePositive: number;
}

// ---------------------------------------------------------------------------
// Costs, decision, severity policy
// ---------------------------------------------------------------------------

export interface DecisionConfig {
	/** Posterior at or above which an above-threshold detection becomes `escalate`. */
	escalateAbove: number;
	/** Review band is [threshold * reviewFraction, threshold). */
	reviewFraction: number;
}

export interface FeatureContribution {
	/** After the per-feature cap. */
	capped: number;
	/** After correlation dampening. What actually enters the sum. */
	dampened: number;
	feature: string;
	group?: string;
	/** Why this row contributed nothing, when it did not. */
	ignored?: "duplicate" | "unknownFeature";
	/** Straight from the strength table, signed by `value`. */
	raw: number;
	strength: ObservationStrength;
	value: ObservationValue;
}

/**
 * Token counts persisted with a pass. Same shape as the client's `GeminiUsage`
 * (which lives in `@sitescene/tokens`, a package this one cannot import).
 */
export interface GeminiUsageSnapshot {
	candidatesTokens: number;
	promptTokens: number;
	promptTokensByModality: Record<string, number>;
	thoughtsTokens: number;
	totalTokens: number;
}

export type HourBucket = "afternoon" | "evening" | "morning" | "night";

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

export interface LikelihoodResult {
	blindContribution: number;
	perFeature: FeatureContribution[];
	/** Sum of dampened feature contributions plus the blind contribution. Uncapped. */
	total: number;
}

/** Season and hour bucket derived from the capture time in the location's timezone. */
export interface LocalContext {
	hourBucket: HourBucket;
	localHour: number;
	season: Season;
	/** IANA zone actually used; "UTC" when the location has none. */
	timezone: string;
	/** True when the location had no timezone and UTC was used instead. */
	timezoneMissing: boolean;
}

export interface LrConfig {
	/**
	 * Multiplier applied to an `absent` observation's strength. A feature that
	 * was looked for and not found is evidence against, but weaker than a
	 * positive confuser signal.
	 */
	absentScale: number;
	/** Log-LR added when the blind pass's top class is the candidate. */
	blindPassAgreement: number;
	/** Log-LR added when the blind pass's top class is one of the confusers. */
	blindPassDisagreement: number;
	/** Log-LR added when the blind pass saw nothing on the list. */
	blindPassNone: number;
	/** Log-LR added when the blind pass named a class unrelated to the candidate. */
	blindPassUnrelated: number;
	/**
	 * Multiplier applied to the blind-pass contribution by the support the
	 * blind pass itself stated for its top answer. A decisive "nothing here"
	 * from the stronger model is worth the full `blindPassNone`; a weak one is
	 * worth a fraction. A missing support reads as `moderate`.
	 */
	blindSupportScale: Record<ObservationStrength, number>;
	/**
	 * Ceiling on how far the blind pass may pull against a confident anchored
	 * verdict, as a fraction of that verdict's magnitude. It may reduce the
	 * anchored reading but not reverse it. Only engages when the two disagree
	 * and the anchored sum is worth at least one `strong` observation, so a
	 * weak anchored signal stays fully overridable and a verification with no
	 * observations at all is untouched.
	 */
	blindVetoFraction: number;
	/**
	 * Multiplier applied to the 2nd, 3rd, ... strongest feature within a
	 * correlation group (x^1, x^2, ...), so redundant features do not multiply
	 * as if independent.
	 */
	correlationDampening: number;
	/** groupKey → feature keys. Usually derived from the discriminator spec. */
	correlationGroups?: Record<string, string[]>;
	/**
	 * Intentionally absent. Per-feature caps bound each contribution; the sum
	 * is left uncapped so strong evidence can always overcome a hostile prior.
	 * See `computeLogLikelihoodRatio`.
	 */
	globalCap: null;
	/** Absolute clamp on any single feature's contribution. */
	perFeatureCap: number;
	/** Natural-log LR granted per strength bucket. */
	strengthToLogLr: Record<ObservationStrength, number>;
}

export interface Observation {
	/** Which confuser the feature points at, when `value` is `supportsConfuser`. */
	confuserClassId?: string;
	feature: string;
	note?: string;
	strength: ObservationStrength;
	value: ObservationValue;
}

// ---------------------------------------------------------------------------
// Likelihood-ratio config and output
// ---------------------------------------------------------------------------

export type ObservationStrength =
	| "decisive"
	| "moderate"
	| "none"
	| "strong"
	| "weak";

/**
 * What the anchored pass may say about one discriminating feature. It reports
 * which way the feature points and how clearly; it never reports a verdict.
 */
export type ObservationValue =
	| "absent"
	| "occluded"
	| "supportsCandidate"
	| "supportsConfuser";

export interface PriorResolution {
	chain: ScopeChainEntry[];
	localContext: LocalContext;
	logPriorOdds: number;
	/** Which level produced the number, or the fallback that did. */
	resolvedFrom: "classDefault" | "constant" | VerificationScopeType;
	/** Id of the row that won, when one did. */
	rowId?: string;
	source: "default" | "measured" | "seeded";
	tableVersion: number;
}

// ---------------------------------------------------------------------------
// Canonicalization and prior resolution outputs
// ---------------------------------------------------------------------------

/** One level of a resolution walk, kept on the record so a surprising decision can be traced. */
export interface ScopeChainEntry {
	/** How many candidate rows existed at this level before time filtering. */
	candidates: number;
	matched: boolean;
	scopeRef?: string;
	scopeType: VerificationScopeType;
}

export type Season = "autumn" | "spring" | "summer" | "winter";

/**
 * How a decision maps onto the 1-10 severity the rest of the system reads.
 * Down-ranking clamps to a ceiling so within-band ordering survives; a
 * detection already under the ceiling is left alone. Escalation raises to at
 * most `escalateTo`, so the verifier can never mint a critical event alone.
 */
export interface SeverityPolicy {
	ceilings: {
		lowPriorityLog: number;
		reviewQueue: number;
	};
	/**
	 * Most a costly class's floor may rise, in severity points. Bounds the lift
	 * so no cost ratio can drag a down-ranked detection back up to its ceiling.
	 */
	consequenceLiftMax: number;
	/**
	 * Severity points the down-rank floor rises per decade of
	 * falseNegative:falsePositive. Keeps consequence visible after the routing
	 * decision has thrown belief away.
	 */
	consequenceLiftPerDecade: number;
	escalateTo: number;
	/**
	 * Severity points a down-rank loses per decade of posterior odds below the
	 * class's alert line. Zero above the line; the value is what turns a bucket
	 * into a spread.
	 */
	marginSlope: number;
}

/**
 * Routing decision. Changes where a detection surfaces, never whether it
 * exists. `escalate` is `alertNow` with enough belief and enough consequence
 * that the verifier may raise severity (bounded by `SeverityPolicy.escalateTo`).
 */
export type VerificationDecision =
	| "alertNow"
	| "escalate"
	| "lowPriorityLog"
	| "reviewQueue";

// ---------------------------------------------------------------------------
// Reserved class ids
// ---------------------------------------------------------------------------

/** Canonicalization found no class. Routed to the unknown-label queue, never dropped. */
export const UNKNOWN_CLASS_ID = "unknown";
/** The blind pass or classifier saw nothing on the list. */
export const NONE_CLASS_ID = "none";

// ---------------------------------------------------------------------------
// Mode
// ---------------------------------------------------------------------------

/** Per-tenant rollout switch, stored on `TenantSettings`. */
export enum PlausibilityMode {
	/** Verifier rewrites measurement severity and emits `measurement.updated`. */
	Live = "live",
	/** Verifier ignores the tenant entirely; no row, no Gemini call. */
	Off = "off",
	/** Verifier runs the full chain and records what it would do. Touches nothing else. */
	Shadow = "shadow",
}

/** See `MeasurementVerification.findingKind`. */
export type MeasurementFindingKind = "falseAlarm" | "reclassified";

/** Block persisted on a Measurement once live mode has applied a decision. */
export interface MeasurementVerification {
	appliedAt: Date;
	decision: VerificationDecision;
	/**
	 * Plain-language conclusion about what the footage shows, composed from
	 * the verifier's own observations, present when the decision down-ranked
	 * the detection. Feeds the event summary prompt so the title and
	 * description stop describing a threat the footage does not support. The
	 * Stage 1 wording it replaces stays on the measurement and on the
	 * verification row; only the user-facing summary changes.
	 */
	finding?: string;
	/**
	 * How the summary prompt reads `finding`. Absent means `falseAlarm`, the
	 * only kind the pipeline verifier writes. `reclassified` is a staff
	 * reanalysis that found the footage shows a different class than Stage 1
	 * named.
	 */
	findingKind?: MeasurementFindingKind;
	/** Severity as Stage 1 emitted it, before the verifier rewrote it. */
	originalSeverity: number;
	/** Absent on a staff override that no measured belief backed. */
	posterior?: number;
	/** Set when a staff reanalysis, not the pipeline verifier, wrote this. */
	reanalysis?: Ref<{ id: string }>;
	/**
	 * The pipeline verification behind it. Absent when a staff reanalysis
	 * wrote a measurement the pipeline never verified (below the alert line).
	 */
	verification?: Ref<{ id: string }>;
}
